import React, { FC, useState } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Button } from '@bphxd/ds-core-react';
import { EMPTY_WORKPACK } from '../../../lib/constants';
import { TWorkpack } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import { renderOptions } from '../../../lib/util/render';
import { WORKPACK_STATUS_ENUM, WORKPACK_TYPE_ENUM } from '../../../lib/constants/dbConstants';
import { WorkpackStatusEnum, WorkpackTypeEnum } from '../../../graph-queries/__generated__/graphql';

const WorkpackForm: FC<{
  defaultValues?: TWorkpack;
  onSubmit: (workpack: TWorkpack) => Promise<unknown>;
  onClose: () => void;
  type: 'add' | 'edit';
  isLoading?: boolean;
}> = ({ defaultValues, onSubmit, type, onClose, isLoading }) => {
  const asset = useSelectedAsset();
  const [isVendor, setIsVendor] = useState(defaultValues?.type === WorkpackTypeEnum.Vendor);
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    const data = new FormData(event.target as HTMLFormElement);
    const inputData = Object.fromEntries(data) as Record<string, string> & {
      type: WorkpackTypeEnum;
      status: WorkpackStatusEnum;
    };

    const workpack: TWorkpack = {
      ...EMPTY_WORKPACK,
      ...inputData,
      assetId: asset?.assetId || -1,
      workpackId: parseInt(inputData.workpackId),
    };
    if (defaultValues?.workpackId) workpack.workpackId = defaultValues.workpackId;

    onSubmit(workpack)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };
  return (
    <Form className="row g-3 px-5" onSubmit={handleSubmit}>
      <div className="col-md-12">
        <FormGroup>
          <Label for="workpack-title">Workpack Title</Label>
          <Input
            type="text"
            name="title"
            id="workpack-title"
            required
            defaultValue={defaultValues?.['title'] || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="workpack-type">Workpack Type</Label>
          <Input
            type="select"
            name="type"
            id="workpack-type"
            className="form-select-placeholder-hack"
            required
            role="combobox"
            onChange={(e) => {
              setIsVendor(e.target?.value === 'Vendor');
            }}
            defaultValue={defaultValues?.['type'] || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(WORKPACK_TYPE_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="workpack-number">Workpack Number</Label>
          <Input
            type="text"
            name="workpackNumber"
            id="workpack-number"
            required
            defaultValue={defaultValues?.workpackNumber || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="workpack-status">Workpack Status</Label>
          <Input
            type="select"
            name="status"
            id="workpack-status"
            className="form-select-placeholder-hack"
            required
            role="combobox"
            defaultValue={defaultValues?.status || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(WORKPACK_STATUS_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="workpack-comments" className="label-optional">
            Comments
          </Label>
          <Input
            type="textarea"
            name="comments"
            id="workpack-comments"
            defaultValue={defaultValues?.comments || ''}
          />
        </FormGroup>
      </div>
      {isVendor && (
        <>
          <div className="col-md-6">
            <FormGroup>
              <Label for="vendor">Vendor Name</Label>
              <Input
                type="text"
                name="vendor"
                id="vendor"
                defaultValue={defaultValues?.vendor || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="vendor-job-number">Vendor Job Number</Label>
              <Input
                name="vendorJobNumber"
                id="vendor-job-number"
                defaultValue={defaultValues?.vendorJobNumber}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="sor-number">SOR Number</Label>
              <Input name="sorNumber" id="sor-number" defaultValue={defaultValues?.sorNumber} />
            </FormGroup>
          </div>
        </>
      )}
      {type === 'add' ? (
        <div className="col-md-12">
          <Button
            type="submit"
            size="lg"
            className="w-100 my-4"
            theme="darker"
            level="primary"
            disabled={isLoading}
          >
            Add new workpack
          </Button>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-md-6">
            <Button
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="tertiary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          <div className="col-md-6">
            <Button
              type="submit"
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="primary"
              disabled={isLoading}
            >
              Save changes
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
};

export default WorkpackForm;
